import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import {
  getNotifications,
  getUnreadNotificationCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification,
} from "../services/notificationService";
import { useAuth } from "./authContext";
import { toast } from "sonner";

export const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [filters, setFiltersState] = useState({
    is_read: null, // null = all, true, false
    notification_type: "",
    priority: "",
  });

  const pollingRef = useRef(null);

  // Fetch unread count for badge
  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;
    try {
      const data = await getUnreadNotificationCount();
      if (typeof data?.unread_count === "number") {
        setUnreadCount(data.unread_count);
      }
    } catch (err) {
      console.error("Failed to fetch unread notification count:", err);
    }
  }, [user]);

  // Fetch notifications list
  const fetchNotificationsList = useCallback(
    async ({ targetPage = 1, append = false, activeFilters = filters } = {}) => {
      if (!user) return;

      if (append) {
        setIsFetchingMore(true);
      } else {
        setIsLoading(true);
      }

      try {
        const params = {
          page: targetPage,
          page_size: 15,
        };

        if (activeFilters.is_read !== null && activeFilters.is_read !== undefined && activeFilters.is_read !== "") {
          params.is_read = activeFilters.is_read;
        }
        if (activeFilters.notification_type) {
          params.notification_type = activeFilters.notification_type;
        }
        if (activeFilters.priority) {
          params.priority = activeFilters.priority;
        }

        const data = await getNotifications(params);
        const results = data?.results || (Array.isArray(data) ? data : []);
        const total = data?.count ?? results.length;
        const next = data?.next;

        setTotalCount(total);
        setHasMore(Boolean(next));
        setPage(targetPage);

        setNotifications((prev) => {
          if (append) {
            // Avoid duplicate IDs
            const existingIds = new Set(prev.map((item) => item.id));
            const newUniqueItems = results.filter((item) => !existingIds.has(item.id));
            return [...prev, ...newUniqueItems];
          }
          return results;
        });
      } catch (err) {
        console.error("Failed to load notifications:", err);
      } finally {
        setIsLoading(false);
        setIsFetchingMore(false);
      }
    },
    [user, filters]
  );

  // Load next page
  const loadMore = useCallback(async () => {
    if (!hasMore || isFetchingMore || isLoading) return;
    await fetchNotificationsList({ targetPage: page + 1, append: true });
  }, [hasMore, isFetchingMore, isLoading, page, fetchNotificationsList]);

  // Update filters and reload from page 1
  const updateFilters = useCallback(
    (newFilters) => {
      setFiltersState((prev) => {
        const next = { ...prev, ...newFilters };
        fetchNotificationsList({ targetPage: 1, append: false, activeFilters: next });
        return next;
      });
    },
    [fetchNotificationsList]
  );

  // Mark single notification as read
  const markAsRead = useCallback(
    async (notificationId) => {
      let wasUnread = false;

      // Optimistic update
      setNotifications((prev) =>
        prev.map((item) => {
          if (item.id === notificationId) {
            if (!item.is_read) wasUnread = true;
            return { ...item, is_read: true, read_at: new Date().toISOString() };
          }
          return item;
        })
      );

      if (wasUnread) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }

      try {
        await markNotificationAsRead(notificationId);
      } catch (err) {
        console.error("Failed to mark notification as read:", err);
        // Sync true count on error
        fetchUnreadCount();
      }
    },
    [fetchUnreadCount]
  );

  // Mark all or selected as read
  const markAllAsRead = useCallback(
    async (notificationIds = null) => {
      // Optimistic update
      if (Array.isArray(notificationIds) && notificationIds.length > 0) {
        const idsSet = new Set(notificationIds);
        let unreadModified = 0;
        setNotifications((prev) =>
          prev.map((item) => {
            if (idsSet.has(item.id)) {
              if (!item.is_read) unreadModified += 1;
              return { ...item, is_read: true, read_at: new Date().toISOString() };
            }
            return item;
          })
        );
        setUnreadCount((prev) => Math.max(0, prev - unreadModified));
      } else {
        setNotifications((prev) =>
          prev.map((item) => ({ ...item, is_read: true, read_at: new Date().toISOString() }))
        );
        setUnreadCount(0);
      }

      try {
        const res = await markAllNotificationsAsRead(notificationIds);
        toast.success(res?.message || "Notifications marked as read");
      } catch (err) {
        console.error("Failed to mark all notifications as read:", err);
        toast.error("Failed to mark notifications as read");
        fetchUnreadCount();
      }
    },
    [fetchUnreadCount]
  );

  // Dismiss / Delete single notification
  const dismissNotification = useCallback(
    async (notificationId) => {
      let wasUnread = false;

      // Optimistic removal
      setNotifications((prev) =>
        prev.filter((item) => {
          if (item.id === notificationId) {
            if (!item.is_read) wasUnread = true;
            return false;
          }
          return true;
        })
      );
      setTotalCount((prev) => Math.max(0, prev - 1));

      if (wasUnread) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }

      try {
        await deleteNotification(notificationId);
        toast.success("Notification removed");
      } catch (err) {
        console.error("Failed to delete notification:", err);
        toast.error("Failed to delete notification");
        fetchNotificationsList({ targetPage: 1, append: false });
        fetchUnreadCount();
      }
    },
    [fetchNotificationsList, fetchUnreadCount]
  );

  // Initialize and periodic polling for unread badge
  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      setNotifications([]);
      return;
    }

    fetchUnreadCount();

    // Setup polling every 60 seconds when visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchUnreadCount();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    pollingRef.current = setInterval(() => {
      if (document.visibilityState === "visible") {
        fetchUnreadCount();
      }
    }, 60000);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [user, fetchUnreadCount]);

  return (
    <NotificationContext.Provider
      value={{
        unreadCount,
        notifications,
        totalCount,
        page,
        hasMore,
        isLoading,
        isFetchingMore,
        filters,
        setFilters: updateFilters,
        fetchNotifications: fetchNotificationsList,
        loadMore,
        fetchUnreadCount,
        markAsRead,
        markAllAsRead,
        dismissNotification,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within a NotificationProvider");
  }
  return context;
};
